import { Component, forwardRef, inject, Input, OnInit, Optional, SkipSelf } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ControlContainer, FormGroupDirective, NG_VALUE_ACCESSOR, NgForm } from '@angular/forms';
import { BaseInput, controlContainerFactory } from '@ui-v2/atoms/inputs/base-input';
import { DropdownInput, UiFormModule } from '@ui/form';
import { AsyncPipe } from '@angular/common';
import { map } from 'rxjs/operators';
import { SelectInput } from '@ui-v2/atoms/inputs/select-input/select-input.component';
import { SelectInputGroup } from '@ui-v2/atoms/inputs/types/select-input.types';
import { FilingOptionsStore } from '@app/services/filing-options/store/filing-options.store';
import { FilingOption } from '@app/services/filing-options/types/filing-options';
import { DbUtils, StringUtils } from '@core/utils';
import { computed } from '@angular/core';

export const filingOptionsFactory =
    (translateService: TranslateService) =>
    ({ id, filingType, diy }: FilingOption): DropdownInput<string> => {
        const labelKey = 'V2.SERVICES.WATER.FILING_OPTIONS.' + StringUtils.camelCaseToUpperSnakeCase(filingType) + '.LABEL';
        const translatedLabel = translateService.instant(labelKey);
        return {
            id: `filing-option-${id}`,
            name: 'filing-options',
            value: id,
            labelResource: diy ? `DIY - ${translatedLabel}` : translatedLabel,
        };
    };

export const filingOptionsForMobileFactory =
    (translateService: TranslateService) =>
    (filingOptions: FilingOption[]): SelectInputGroup<string> => ({
        name: 'filing-options',
        options: filingOptions.map((option) => {
            const labelKey = 'V2.SERVICES.WATER.FILING_OPTIONS.' + StringUtils.camelCaseToUpperSnakeCase(option?.filingType) + '.LABEL';
            const translatedLabel = translateService.instant(labelKey);
            return {
                value: option.id,
                labelResource: option?.diy ? `DIY - ${translatedLabel}` : translatedLabel,
            };
        }),
    });

@Component({
    selector: 'app-filing-options-input',
    standalone: true,
    imports: [TranslateModule, UiFormModule, SelectInput],
    template: `
        @if (forMobile) {
            <div class="relative">
                <ui-v2-select-input
                    [(ngModel)]="innerModel"
                    (ngModelChange)="onModelChange($event)"
                    (blur)="onBlur()"
                    [disabled]="innerDisabled"
                    [legend]="'V2.SERVICES.FILING_OPTIONS.LEGEND' | translate"
                    [placeholder]="'V2.SERVICES.FILING_OPTIONS.PLACEHOLDER' | translate"
                    [inputGroup]="filingOptionsForMobile()"
                ></ui-v2-select-input>
            </div>
        } @else {
            <ui-searchable-dropdown-input
                [options]="filingOptions()"
                [placeholder]="'V2.SERVICES.FILING_OPTIONS.PLACEHOLDER' | translate"
                [label]="'V2.SERVICES.FILING_OPTIONS.LEGEND' | translate"
                [(ngModel)]="innerModel"
                (ngModelChange)="onModelChange($event)"
                (blur)="onBlur()"
                [disabled]="innerDisabled"
                width="100%"
                ngDefaultControl
            ></ui-searchable-dropdown-input>
        }
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FilingOptionsInputComponent),
            multi: true,
        },
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [
                [new Optional(), new SkipSelf(), FormGroupDirective],
                [new Optional(), new SkipSelf(), NgForm],
            ],
        },
    ],
})
export class FilingOptionsInputComponent extends BaseInput<string> implements OnInit {
    @Input() public forMobile = false;
    @Input() public serviceTypeId: string;
    @Input() public supplierId: string;

    private readonly filingOptionsStore = inject(FilingOptionsStore);
    private readonly translateService = inject(TranslateService);

    public readonly filingOptions = computed(() =>
        this.filingOptionsStore.filingOptions().map(filingOptionsFactory(this.translateService)),
    );

    public readonly filingOptionsForMobile = computed(() =>
        filingOptionsForMobileFactory(this.translateService)(this.filingOptionsStore.filingOptions()),
    );

    public ngOnInit(): void {
        if (this.serviceTypeId && this.supplierId) {
            this.loadFilingOptions();
        }
    }

    private loadFilingOptions(): void {
        this.filingOptionsStore.loadFilingOptions(this.serviceTypeId, this.supplierId);
    }

    public writeValue(value: string): void {
        this.innerModel = value ?? null;
    }

    public onModelChange(value: string): void {
        this.propagateChange(value);
    }
}
